<template>
  <div class="lg:!mx-0 flex">
    <div class="space-y-4 p-4 flex max-h-content w-screen min-h-content">
      <div class="h-full overflow-y-auto w-full grid grid-rows-[min-content_1fr] grid-cols-2 gap-2">
        <div
          v-if="!setFullHeight"
          class="bg-white rounded-xl shadow-sm h-full"
        >
          <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-white">
                Project Summary
              </h2>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
              </div>
            </div>
          </div>

          <div class="p-4">
            <div class="space-y-4 divide-y divide-gray-100">
              <div class="flex justify-between items-center py-3">
                <span class="text-sm text-gray-600 font-medium">Total Contract Value</span>

                <span class="text-sm font-bold text-orange-600">{{ $tcc(totalContractValue) }}</span>
              </div>

              <div class="flex justify-between items-center py-3">
                <span class="text-sm font-medium text-gray-600">Total Budget Assigned</span>

                <span class="text-sm font-bold text-gray-900">{{ $tcc(totalBudget) }}</span>
              </div>

              <div class="flex justify-between items-center py-3">
                <span class="text-sm font-medium text-gray-600">Total Awarded</span>

                <span class="text-sm font-bold text-blue-600">{{ $tcc(totalAwarded) }}</span>
              </div>

              <div class="flex justify-between items-center py-3">
                <span class="text-sm font-medium text-gray-600">Profit/Loss</span>

                <span
                  class="text-sm font-bold"
                  :class="totalProfit >= 0 ? 'text-green-600' : 'text-red-600'"
                >
                  {{ $tcc(totalProfit) }}
                </span>
              </div>

              <div class="flex justify-between items-center py-3">
                <span class="text-sm font-medium text-gray-600">Total Packages</span>

                <span class="text-sm font-bold text-gray-900">{{ packages.length }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!setSubcontractorsFullHeight"
          class="bg-white rounded-xl shadow-sm"
          :class="{ 'col-start-2': setFullHeight }"
        >
          <div class="rounded-t-xl bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5">
            <div class="flex items-center justify-between">
              <h2 class="text font-semibold text-white">
                {{ $t('Subcontract.SubcontractDashboard.awardedValue') }}
              </h2>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
              </div>
            </div>
          </div>

          <div class="p-2">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center space-x-4 px-2">
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-blue-500 rounded-full" />

                  <span class="text-sm text-gray-600 font-medium">{{ $t('Subcontract.SubcontractDashboard.awarded') }}</span>
                </div>

                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-red-500 rounded-full" />

                  <span class="text-sm text-gray-600 font-medium">{{ $t('Subcontract.SubcontractDashboard.remaining') }}</span>
                </div>
              </div>

              <div class="text-right">
                <div class="text font-bold text-gray-900">
                  {{ $tcc(totalAwarded) }}
                </div>

                <div class="text-xs text-gray-500">
                  of {{ $tcc(totalBudget) }}
                </div>
              </div>
            </div>

            <div class="h-48">
              <Pie
                :data="chartData"
                :options="chartOptions"
                :chart-id="chartId"
                :dataset-id-key="datasetIdKey"
                :plugins="plugins"
                :css-classes="cssClasses"
                :styles="styles"
                :width="width"
                :height="height"
              />
            </div>

            <!-- Progress Stats -->
            <div class="mt-6 grid grid-cols-2 gap-4">
              <div class="bg-blue-50 rounded-lg p-4">
                <div class="text-sm text-blue-600 font-medium">
                  Awarded
                </div>

                <div class="text-xl font-bold text-blue-900">
                  {{ totalBudget > 0 ? ((totalAwarded / totalBudget) * 100).toFixed(1) : '0' }}%
                </div>
              </div>

              <div class="bg-red-50 rounded-lg p-4">
                <div class="text-sm text-red-600 font-medium">
                  Remaining
                </div>

                <div class="text-xl font-bold text-red-900">
                  {{ $tcc(Math.max(totalBudget - totalAwarded, 0)) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Dashboard Grid -->
        <div :class="[setFullHeight ? 'row-span-2 col-start-1 row-start-1' : 'row-span-1']">
          <div class="bg-white flex flex-col rounded-xl shadow-sm h-full">
            <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                  <div
                    @click="togglePackagesExpand"
                  >
                    <button
                      type="button"
                      class="rounded-full flex items-center justify-center transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="setFullHeight"
                       
                        class="w-6 h-6 text-white">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                       
                        class="w-6 h-6 text-white">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                    </button>
                  </div>

                  <h2 class="font-semibold text-white">
                    {{ $t('Subcontract.SubcontractDashboard.package') }}
                  </h2>
                </div>

                <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-white">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>
                </div>
              </div>
            </div>

            <div class="relative overflow-y-auto flex-1 p-4">
              <div
                v-if="indexPackagesActions.is(States.LOADING)"
                class="flex flex-col items-center justify-center h-full"
              >
                <p class="text-sm text-gray-500 font-medium text-center">
                  Loading packages...
                </p>

                <div class="flex flex-col items-center mt-4">
                  <AppSpinner />
                </div>
              </div>

              <div
                v-else-if="packages.length === 0 && indexPackagesActions.is(States.COMPLETE)"
                class="text-center py-8"
              >
                <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-8 h-8 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>
                </div>

                <h3 class="text-lg font-semibold text-gray-900 mb-2">
                  No Packages Found
                </h3>

                <p class="text-gray-600">
                  No packages have been created for this project yet.
                </p>
              </div>

              <div
                v-else
                class="space-y-3 absolute inset-0 overflow-y-auto p-4"
              >
                <div
                  v-for="projectPackage in packages"
                  :key="projectPackage.id"
                  class="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors border border-gray-200 hover:border-blue-300"
                  @click="router.push({ name: 'PackagesDashboard', params: { packageId: projectPackage.id } })"
                >
                  <h3 class="font-semibold text-gray-900">
                    {{ projectPackage.name }}
                  </h3>

                  <div class="grid grid-cols-2 gap-2 text-xs mt-2">
                    <div class="">
                      <span class="text-gray-600">Budget:&nbsp;</span>

                      <span class="font-semibold text-gray-900">{{ $tcc(projectPackage.budgetIncludingPackages) }}</span>
                    </div>


                    <div class="flex items-center justify-end">
                      <span class="text-gray-500">Profit:&nbsp;</span>

                      <span class="font-semibold text-green-600">
                        {{ $tcc(projectPackage.budgetIncludingPackages - projectPackage.totalAwarded) }}
                      </span>
                    </div>


                    <div class="grid grid-cols-2 gap-2 col-span-2">
                      <div class="w-full bg-gray-200 rounded-full h-2 col-span-2">
                        <div
                          class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          :style="{ width: projectPackage.budgetIncludingPackages > 0 ? `${Math.min((projectPackage.totalAwarded / projectPackage.budgetIncludingPackages) * 100, 100)}%` : '0%' }"
                        />
                      </div>

                      <span class="font-semibold text-blue-600">
                        <span class="text-gray-500">Awarded:&nbsp;</span>

                        <span>
                          {{ $tcc(projectPackage.totalAwarded) }}
                        </span>
                      </span>

                      <div class="text-right">
                        <span class="text-gray-600">Subcontractor:&nbsp;</span>

                        <span class="font-medium text-gray-900">{{ projectPackage.subcontractor?.name || 'Not assigned' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div :class="[setSubcontractorsFullHeight ? 'row-span-2 col-start-2 row-start-1' : 'row-span-1']">
          <div class="bg-white flex flex-col rounded-xl shadow-sm h-full">
            <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                  <div
                    @click="toggleSubcontractorsExpand"
                  >
                    <button
                      type="button"
                      class="rounded-full flex items-center justify-center transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="setSubcontractorsFullHeight"
                       
                        class="w-6 h-6 text-white">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                       
                        class="w-6 h-6 text-white">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                    </button>
                  </div>

                  <h2 class="font-semibold text-white">
                    {{ $t('Subcontract.SubcontractDashboard.subcontractor') }}
                  </h2>
                </div>

                <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
</svg>
                </div>
              </div>
            </div>

            <div class="relative overflow-y-auto flex-1 p-4">
              <div
                v-if="indexPackagesActions.is(States.LOADING)"
                class="flex flex-col items-center justify-center h-full"
              >
                <p class="text-sm text-gray-500 font-medium text-center">
                  Loading subcontractors...
                </p>

                <div class="flex flex-col items-center mt-4">
                  <AppSpinner />
                </div>
              </div>

              <div
                v-else-if="subcontractors.length === 0 && indexPackagesActions.is(States.COMPLETE)"
                class="text-center py-8"
              >
                <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-8 h-8 text-gray-400">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
</svg>
                </div>

                <h3 class="text-lg font-semibold text-gray-900 mb-2">
                  {{ $t('Subcontract.SubcontractDashboard.noSubcontractorsFound') }}
                </h3>

                <p class="text-gray-600">
                  No subcontractors have been assigned to packages yet.
                </p>
              </div>

              <div
                v-else
                class="space-y-3 absolute inset-0 overflow-y-auto p-4"
              >
                <div
                  v-for="subcontractor in subcontractors"
                  :key="subcontractor.id"
                  class="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <h3 class="font-semibold text-gray-900">
                    {{ subcontractor.name }}
                  </h3>

                  <div class="grid grid-cols-2 gap-2 text-xs mt-2">
                    <div class="flex text-xs">
                      <span
                        v-if="subcontractor.contractValue"
                        class="text-gray-600"
                      >Contract Value:&nbsp;</span>

                      <div
                        v-if="subcontractor.contractValue"
                        class="font-semibold text-gray-900"
                      >
                        {{ $tcc(subcontractor.contractValue) }}
                      </div>

                      <button
                        v-else-if="projectsStore.project?.unarchived"
                        class="btn btn--tertiary-blue p-0"
                        type="button"
                        @click="openSupplierModal(subcontractor)"
                      >
                        {{ $t('Subcontract.SubcontractDashboard.setContractValue') }}
                      </button>

                      <span
                        v-else
                        class="text-gray-500"
                      >{{ $t('Subcontract.SubcontractDashboard.notSet') }}</span>
                    </div>

                    <template v-if="subcontractor.contractValue">
                      <div class="text-right">
                        <span class="text-gray-600">Remaining:&nbsp;</span>

                        <span class="font-semibold text-orange-600">{{ $tcc(subcontractor.contractValue - subcontractor.awardedAmount) }}</span>
                      </div>


                      <div class="w-full bg-gray-200 rounded-full h-2 col-span-2">
                        <div
                          class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          :style="{ width: subcontractor.contractValue > 0 ? `${Math.min((subcontractor.awardedAmount / subcontractor.contractValue) * 100, 100)}%` : '0%' }"
                        />
                      </div>

                      <span class="text-xs text-gray-500 block">
                        <span class="text-gray-600">Awarded:&nbsp;</span>

                        <span class="font-semibold text-blue-600">{{ $tcc(subcontractor.awardedAmount) }}</span>
                      </span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ProjectSupplierModal
      v-if="showSupplierModal"
      :project-supplier="selectedProjectSupplier"
      :project-id="projectsStore.project.id"
      @close="showSupplierModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import ProjectSupplierModal from '@/components/ProjectSettings/VendorsTab/ProjectSupplierModal.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t, tcc } from '@/i18n';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { computed, ref, watch } from 'vue';
import { Pie } from 'vue-chartjs';
import { useRouter } from 'vue-router';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
);

const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();
const tradesStore = useTradesStore();
const variationOrdersStore = useVariationOrdersStore();
const router = useRouter();
const project = computed(() => projectsStore.project);
const indexPackagesActions = useStoreApiAction(packagesStore.index);
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const fetchVariationsAction = useStoreApiAction(variationOrdersStore.fetchVariationOrders);

useBoqLockRedirect();

watch(() => project.value?.id, (newValue) => {
  if(newValue) {
    indexPackagesActions.request(newValue);

    fetchTradesAction.request(newValue, { boq_type: 'direct' }).catch((error) => {
      console.log(error);
    });

    fetchVariationsAction.request(newValue).catch((error) => {
      console.log(error);
    });
  }
}, { immediate: true });

const packages = computed(() => {
  return packagesStore.models
    .where('project_id', project.value?.id)
    .with('subcontractor', (query) => {
      query.with('projects', (query) => {
        query.where('project_id', project.value?.id)
          .with('supplier');
      });
    })
    .get();
});

// Group packages by subcontractor to calculate subcontractor summary
const subcontractors = computed(() => {
  const subcontractorMap = new Map();

  packages.value.forEach((projectPackage) => {
    if(projectPackage.subcontractor) {
      const subcontractorId = projectPackage.subcontractor.id;

      if(!subcontractorMap.has(subcontractorId)) {
        subcontractorMap.set(subcontractorId, {
          id: subcontractorId,
          name: projectPackage.subcontractor.name,
          contractValue: projectPackage.subcontractor.projects?.[0]?.contract_value || 0,
          awardedAmount: 0,
          subcontractor: projectPackage.subcontractor,
        });
      }

      // Add the awarded amount from this package
      subcontractorMap.get(subcontractorId).awardedAmount += projectPackage.totalAwarded;
    }
  });

  return Array.from(subcontractorMap.values());
});

const totalAwarded = computed(() => {
  return packages.value.reduce((total, currentPackage) => {
    return total + currentPackage.totalAwarded;
  }, 0);
});

const totalBudget = computed(() => {
  return packages.value.reduce((total, currentPackage) => {
    return total + currentPackage.budgetIncludingPackages;
  }, 0);
});

const totalProfit = computed(() => {
  return totalBudget.value - totalAwarded.value;
});

// Get trades for head contract calculations
const trades = computed(() => {
  return tradesStore.models.where('project_id', project.value?.id).where('boq_type', 'direct').get();
});

// Get approved variation orders
const approvedVariationOrders = computed(() => {
  return variationOrdersStore.models.where('project_id', project.value?.id).where(
    'status',
    VariationOrderStatus.approved,
  ).get();
});

// Calculate total budget from trades (head contract budget)
const headContractTotalBudget = computed(() => {
  return trades.value.reduce((total, current) => {
    return total + current.totalBudget;
  }, 0);
});

// Calculate total approved variation orders
const variationOrdersTotalApproved = computed(() => {
  return approvedVariationOrders.value
    .reduce((total, variationOrder) => {
      if(variationOrder.budget) {
        return total + variationOrder.budget;
      }

      return total;
    }, 0);
});

// Calculate total contract value (head contract budget + approved variations)
const totalContractValue = computed(() => {
  return headContractTotalBudget.value + variationOrdersTotalApproved.value;
});

const chartData = computed(() => ({
  labels: [
    `${t('Subcontract.SubcontractDashboard.awarded')} ${tcc(totalAwarded.value)} `,
    `${t('Subcontract.SubcontractDashboard.notAwarded')} ${tcc(Math.max(totalBudget.value - totalAwarded.value, 0))}`,
  ],

  datasets: [
    {
      data: [totalAwarded.value, Math.max(totalBudget.value - totalAwarded.value, 0)],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(239, 68, 68, 0.8)',
      ],

      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(239, 68, 68, 1)',
      ],

      hoverOffset: 4,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },

    tooltip: {
      callbacks: {
        label(this: any, tooltipItem: any) {
          return `${tcc(tooltipItem.dataset.data[tooltipItem.dataIndex])}`;
        },
      },
    },
  },

  maintainAspectRatio: false,
};

const chartId = 'awardChart';
const datasetIdKey = 'awardChartDataset';
const plugins = [];
const cssClasses = [];
const styles = {};
const width = 200;
const height = 200;
// ProjectSupplierModal functionality
const showSupplierModal = ref(false);
const selectedProjectSupplier = ref(undefined);

const openSupplierModal = (subcontractor) => {
  selectedProjectSupplier.value = subcontractor.subcontractor.projects?.[0];
  showSupplierModal.value = true;
};

const setFullHeight = ref(false);

const togglePackagesExpand = () => {
  setFullHeight.value = !setFullHeight.value;
};

const setSubcontractorsFullHeight = ref(false);

const toggleSubcontractorsExpand = () => {
  setSubcontractorsFullHeight.value = !setSubcontractorsFullHeight.value;
};
</script>

<style scoped></style>
